import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Collapse,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getGolfSpotHistory } from '../../api/golf';

const GolfSpotHistory = ({ spotData, onSellClick }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const runningRowRef = useRef(null);

  const {
    data: historyData,
    isSpotHistoryError,
    spotHistoryError,
  } = useQuery({
    queryKey: ['golfSpotHistory', user?.token, spotData?.id],
    queryFn: () => getGolfSpotHistory(user?.token, spotData?.id),
    enabled: !!user?.token && !!spotData?.id,
    staleTime: 0,
    refetchInterval: 3000, // 30초마다 자동으로 데이터 갱신
    refetchIntervalInBackground: true, // 백그라운드에서도 갱신
  });

  if (isSpotHistoryError) console.log('스팟 이용 내역 조회 실패: ', spotHistoryError);

  // 시간 포맷팅 함수
  const formatDateTime = (dateTimeString, includeSeconds = false) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      if (includeSeconds) {
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
      }
      return `${hours}:${minutes}`;
    } catch {
      return dateTimeString;
    }
  };

  // 날짜 포맷팅 함수
  const formatDate = dateString => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateString;
    }
  };

  // 이용 시간 계산 함수
  const calculateUsageTime = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end - start;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}${t('minutes')}`;
    } catch {
      return '';
    }
  };

  // 이용권 타입 번역
  const getTicketTypeLabel = courseType => {
    if (!courseType) return '';
    return courseType === 'PASS' ? t('Usage ticket') : courseType;
  };

  // 이용권 카테고리 번역
  const getTicketCategoryLabel = courseType => {
    if (!courseType) return '';
    return courseType === 'PASS' ? t('Individual ticket') : '';
  };

  // 이름에서 숫자 제거 (예: "최현민1234" → "최현민")
  const formatMemberName = memberName => {
    if (!memberName) return t('Guest');
    // 숫자 제거
    return memberName.replace(/\d+/g, '');
  };

  // 모든 상태의 내역 표시 (WAITING, RUNNING 포함), 취소된 예약은 제외, 최신순으로 정렬
  const historyList = Array.isArray(historyData)
    ? historyData
        .filter(row => {
          const status = row.status || row.reservationStatus;
          // 취소된 예약 필터링 (CANCELED)
          return status && !['CANCELED'].includes(status.toUpperCase());
        })
        .reverse() // 최신 데이터가 위에 오도록 역순 정렬
    : [];

  return (
    <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '8px' }}>
      <Button
        fullWidth
        onClick={() => {
          setIsHistoryExpanded(!isHistoryExpanded);
          // 펼쳐진 후 현재 이용중인 행으로 스크롤 이동
          if (!isHistoryExpanded && runningRowRef.current) {
            setTimeout(() => {
              runningRowRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }, 100);
          }
        }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: '16px',
          py: '12px',
          textTransform: 'none',
          color: '#000',
          '&:hover': { backgroundColor: '#F2F2F2' },
        }}>
        <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#000' }}>{t('Spot usage history')}</Typography>
        {isHistoryExpanded ? (
          <KeyboardArrowUpIcon sx={{ color: '#000' }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: '#000' }} />
        )}
      </Button>

      <Collapse in={isHistoryExpanded}>
        <TableContainer sx={{ maxHeight: '325px', overflowY: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: '1404px' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: '66px',
                    minWidth: '66px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Start')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '66px',
                    minWidth: '66px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('End')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '80px',
                    minWidth: '80px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Check-in')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '64px',
                    minWidth: '64px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    px: '8px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Time')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '48px',
                    minWidth: '48px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    px: '8px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Buffer')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '80px',
                    minWidth: '80px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Member name')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '160px',
                    minWidth: '160px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Mobile phone')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                    minWidth: '200px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Usage ticket name')}
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{
                    width: '320px',
                    minWidth: '320px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Usage period')}
                </TableCell>
                <TableCell
                  colSpan={4}
                  sx={{
                    width: '320px',
                    minWidth: '320px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    py: '12px',
                    borderBottom: 'none',
                  }}>
                  {t('Usage ticket')}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{
                  position: 'sticky',
                  top: '43px', // 첫 번째 헤더 행 높이만큼 아래에 위치
                  zIndex: 10,
                  backgroundColor: '#fff',
                }}>
                <TableCell
                  colSpan={8}
                  sx={{
                    p: 0,
                    backgroundColor: '#fff',
                  }}></TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '12px',
                    px: '10px',
                    py: '4px',
                    backgroundColor: '#fff',
                  }}>
                  {t('Start date')}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '12px',
                    px: '10px',
                    py: '4px',
                    backgroundColor: '#fff',
                  }}>
                  {t('Expiration date')}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '12px',
                    px: '10px',
                    py: '4px',
                    backgroundColor: '#fff',
                  }}>
                  {t('Ticket type')}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '12px',
                    px: '10px',
                    py: '4px',
                    backgroundColor: '#fff',
                  }}>
                  {t('Ticket category')}
                </TableCell>
                <TableCell
                  sx={{
                    width: '80px',
                    minWidth: '80px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '12px',
                    px: '10px',
                    py: '4px',
                    backgroundColor: '#fff',
                  }}>
                  {t('Time')}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '12px',
                    px: '10px',
                    py: '4px',
                    backgroundColor: '#fff',
                  }}>
                  {t('Ticket count')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} sx={{ textAlign: 'center', py: '24px', color: '#737373' }}>
                    {t('No usage history')}
                  </TableCell>
                </TableRow>
              ) : (
                historyList.map((row, index) => {
                  // 예약 상태에 따른 배경색 결정
                  const getRowBackgroundColor = () => {
                    const status = row.status || row.reservationStatus;
                    if (status === 'RUNNING') {
                      return '#C7E8EA'; // 현재 이용중
                    } else if (status === 'RESERVED' || status === 'WAITING') {
                      return '#ffffff'; // 예약되어있음
                    }
                    return '#D9D9D9'; // 종료된 것 (기본 색상)
                  };

                  const rowBgColor = getRowBackgroundColor();
                  const status = row.status || row.reservationStatus;
                  const isRunning = status === 'RUNNING';

                  return (
                    <TableRow
                      key={row.reservationId || index}
                      ref={isRunning ? runningRowRef : null}
                      onClick={() => onSellClick && onSellClick(row)}
                      sx={{
                        cursor: onSellClick ? 'pointer' : 'default',
                        backgroundColor: rowBgColor,
                        '&:hover': {
                          backgroundColor: '#58B9C1',
                          color: '#fff',
                          '& .MuiTableCell-root': {
                            color: '#fff',
                          },
                        },
                      }}>
                      <TableCell
                        sx={{
                          width: '66px',
                          minWidth: '66px',
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '12px',
                        }}>
                        {formatDateTime(row.startTime)}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '66px',
                          minWidth: '66px',
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '12px',
                        }}>
                        {formatDateTime(row.endTime)}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '80px',
                          minWidth: '80px',
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '12px',
                        }}>
                        {formatDateTime(row.checkInTime, true)}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '64px',
                          minWidth: '64px',
                          textAlign: 'center',
                          fontSize: '16px',
                          px: '8px',
                          py: '12px',
                        }}>
                        {row.usageMinute
                          ? `${row.usageMinute}${t('minutes')}`
                          : calculateUsageTime(row.startTime, row.endTime)}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '48px',
                          minWidth: '48px',
                          textAlign: 'center',
                          fontSize: '16px',
                          px: '8px',
                          py: '12px',
                        }}>
                        {row.bufferMinute ? `${row.bufferMinute}${t('minutes')}` : '0분'}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '80px',
                          minWidth: '80px',
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '12px',
                        }}>
                        {formatMemberName(row.memberName)}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '160px',
                          minWidth: '160px',
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '12px',
                        }}>
                        {row.memberPhone || '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '200px',
                          minWidth: '200px',
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '12px',
                        }}>
                        {row.coursePassName || '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '8px',
                        }}>
                        {row.passStartTime ? formatDate(row.passStartTime) : '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '8px',
                        }}>
                        {row.passEndTime ? formatDate(row.passEndTime) : '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '8px',
                        }}>
                        {getTicketTypeLabel(row.courseType)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '8px',
                        }}>
                        {getTicketCategoryLabel(row.courseType)}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '80px',
                          minWidth: '80px',
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '8px',
                        }}>
                        {row.usageMinute ? `${row.usageMinute}${t('minutes')}` : '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          py: '8px',
                        }}>
                        {t('Unlimited')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
};

export default GolfSpotHistory;
